import React from 'react';
import { Modal, Button, Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import '../styles/EulaModal.css';

const EulaContent = ({ onAccept, onDecline, isModal = false }) => {
    const navigate = useNavigate();

    const handleAccept = () => {
        if (onAccept) onAccept();
        else navigate('/');
    };

    const handleDecline = () => {
        if (onDecline) onDecline();
        else navigate('/');
    };

    const content = (
        <div 
            className={isModal ? 'eula-content' : 'py-5'}
            data-testid="eula-scroll-container"
        >
            <p>
                <strong>SBMS (Space Cockpit Battle Management System)</strong><br/>
                Version 3.0 — 2025-05-26
            </p>

            <h2>IMPORTANT — READ CAREFULLY:</h2>
            <p>This End User License Agreement (&quot;Agreement&quot;) is a legal agreement between you (either an individual or a single entity) and Saber Astronautics LLC, the developers of the SBMS (Space Cockpit Battle Management System) software (&quot;Licensor&quot;). By installing, copying, or using the SBMS software, you agree to be bound by the terms of this Agreement.</p>
            <p>If you do not agree to the terms of this Agreement, do not use the software.</p>

            <h2>1. License Grant</h2>
            <p>Subject to the terms and conditions of this Agreement, the Licensor hereby grants you a <strong>non-exclusive, non-transferable, revocable license</strong> to use the SBMS software solely for official U.S. Department of Defense (DoD) or U.S. Government purposes, or as explicitly authorized by the Licensor in writing for allied or partner nation use during approved exercises or operations.</p>

            <h2>2. Restrictions</h2>
            <p>You shall not:</p>
            <ul>
                <li>Modify, adapt, translate, reverse engineer, decompile, disassemble, or create derivative works based on the SBMS software, or otherwise attempt to discover its source code or underlying structure, ideas, or algorithms.</li>
                <li>Rent, lease, sublicense, sell, transfer, assign, or otherwise distribute the software to any third party not explicitly authorized under this Agreement.</li>
                <li>Use the software in a manner that violates U.S. laws or regulations, including but not limited to those governing defense technology and export controls.</li>
                <li>Disclose, reproduce, or distribute the software or any part thereof to unauthorized users or entities.</li>
            </ul>

            <h2>3. Ownership</h2>
            <p>All title, ownership rights, and intellectual property rights in and to the SBMS software and any copies thereof remain the exclusive property of the Licensor. This Agreement does not convey to you any rights of ownership in or related to the SBMS software.</p>

            <h2>4. Export Control and Foreign Disclosure</h2>
            <p>You acknowledge that the SBMS software may be subject to U.S. export control laws and regulations, including the International Traffic in Arms Regulations (ITAR) and the Export Administration Regulations (EAR). You agree not to export, re-export, or otherwise transfer the software in violation of any such applicable laws or regulations.</p>
            <p>Use by foreign allied governments is strictly limited to cases authorized in writing by the Licensor and/or relevant U.S. Government authorities.</p>

            <h2>5. Use Limitations</h2>
            <p>The software is provided <strong>only</strong> for use in support of:</p>
            <ul>
                <li>U.S. DoD and Government operations, training, or mission planning;</li>
                <li>Allied or partner nation exercises or test environments with prior written approval;</li>
                <li>No use in commercial, non-governmental, or personal contexts is authorized unless authorized by Saber Astronautics in writing.</li>
            </ul>

            <h2>6. Termination</h2>
            <p>This Agreement is effective until terminated. It will terminate automatically without notice from the Licensor if you fail to comply with any provision of this Agreement. Upon termination, you must cease all use of the software and destroy all copies.</p>

            <h2>7. No Warranty</h2>
            <p>The software is provided <strong>&quot;as is&quot;</strong> without warranty of any kind, either express or implied. The Licensor makes no warranties, express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.</p>

            <h2>8. Limitation of Liability</h2>
            <p>In no event shall the Licensor be liable for any damages, including without limitation direct, indirect, special, incidental, or consequential damages, arising out of the use or inability to use the software, even if advised of the possibility of such damages.</p>

            <h2>9. Governing Law</h2>
            <p>This Agreement shall be governed and interpreted in accordance with the laws of the United States of America. Any disputes shall be subject to the exclusive jurisdiction of the appropriate U.S. federal courts.</p>

            <h2>10. Acknowledgment</h2>
            <p>By installing or using the SBMS software, you acknowledge that you have read this Agreement, understand it, and agree to be bound by its terms and conditions.</p>
            <br/>
        </div>
    );

    const buttons = (
        <div className="d-flex justify-content-end gap-2 mt-4">
            <Button 
                variant="secondary" 
                onClick={handleDecline}
                style={{ 
                    backgroundColor: '#333',
                    borderColor: '#333'
                }}
            >
                Decline
            </Button>
            <Button 
                variant="warning"
                onClick={handleAccept}
                style={{ 
                    backgroundColor: '#f5bc59',
                    borderColor: '#f5bc59'
                }}
            >
                Accept
            </Button>
        </div>
    );

    if (isModal) {
        return (
            <>
                <Modal.Header className="bg-dark text-white border-0">
                    <Modal.Title>End User License Agreement (EULA)</Modal.Title>
                </Modal.Header>
                <Modal.Body className="bg-dark text-white p-0">
                    {content}
                </Modal.Body>
                <Modal.Footer className="bg-dark border-0">
                    {buttons}
                </Modal.Footer>
            </>
        );
    }

    return (
        <Container className="bg-dark text-white min-vh-100">
            <h1 className="text-warning mb-4">End User License Agreement (EULA)</h1>
            {content}
            {buttons}
        </Container>
    );
};

const EulaModal = ({ show, onAccept, onDecline }) => {
    if (!show) return null;

    return (
        <Modal
            show={show}
            onHide={onDecline}
            centered
            size="lg"
            backdrop="static"
            keyboard={false}
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
            dialogClassName="eula-modal"
        >
            <EulaContent onAccept={onAccept} onDecline={onDecline} isModal={true} />
        </Modal>
    );
};

export const EulaPage = () => {
    return <EulaContent />;
};

export default EulaModal; 